import React, { useCallback, useEffect, useState, useMemo } from "react";
import { pagedItems, PAGE_SIZE } from "../utils/adminPaging";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { formatDuration } from "../utils/formatDuration";
import {
  formatMoneyByCurrency,
  moneyByCurrencyFrom,
  formatBatchAvgValue,
  CurrencyTotals,
} from "../utils/currency";
import { TIME_RANGE_OPTIONS, TimeRangeKey, partitionByRange, rangeStart } from "../utils/dateRange";
import TimeRangeBar from "./TimeRangeBar";
import UndatedNote from "./UndatedNote";
import AdminLoadFailed from "./AdminLoadFailed";
import StatsPending from "./admin-stats/StatsPending";
import { payoutSummaryFrom, payoutSummaryUrl } from "./admin-stats/api";
import { useAdminSummary } from "../hooks/useAdminSummary";
import { withQuietRetry } from "../utils/adminRetry";
import { absMediaUrl } from "../utils/mediaUrl";

interface PayoutDj {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
}

interface Payout {
  _id: string;
  batchId: string;
  createdAt: string;
  dj: PayoutDj;
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
  avgResponseMs?: number | null;
  // Per-currency gross/fee/net from the server — the real source for showing
  // each payout in its own settlement currency instead of a blended "$".
  totalsByCurrency?: Record<string, CurrencyTotals>;
}

type SortField = "date" | "gross" | "avg";
type SortDir = "asc" | "desc";

interface Props {
  // Shared, parent-owned time window (persists across the money tabs).
  range: TimeRangeKey;
  onRangeChange: (key: TimeRangeKey) => void;
}

export default function AdminAllPayoutsTab({ range, onRangeChange }: Props) {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [dateSearch, setDateSearch] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    withQuietRetry(() => adminApi.get(`/payouts?limit=${PAGE_SIZE}`))
      .then((r) => {
        const { items, total } = pagedItems<Payout>(r.data);
        setPayouts(items);
        setPayoutsTotal(total);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const loadMore = () => {
    setLoadingMore(true);
    setLoadMoreFailed(false);
    withQuietRetry(() => adminApi.get(`/payouts?limit=${PAGE_SIZE}&skip=${payouts.length}`))
      .then((r) => {
        const { items, total } = pagedItems<Payout>(r.data);
        setPayouts((prev) => [...prev, ...items]);
        setPayoutsTotal(total);
      })
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  };

  // The KPI cards add up EVERY payout on the server, from the same first
  // instant the table filters on. A server without the summary route (404)
  // gets the old sum over the loaded rows below.
  const since = useMemo(() => rangeStart(range), [range]);
  const summary = useAdminSummary(payoutSummaryUrl(since), payoutSummaryFrom);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortArrow = (field: SortField) => {
    if (sortField !== field) return <span className="sort-arrows muted-text">↕</span>;
    return <span className="sort-arrows">{sortDir === "desc" ? "↓" : "↑"}</span>;
  };

  // Apply the shared time window first (by payout date), then the existing
  // free-text date search + sort. Undated payouts are excluded from a bounded
  // window and kept only under All Time (surfaced via UndatedNote) — the same
  // money-never-disappears contract as the other tabs.
  const { inRange, undatedCount } = useMemo(() => {
    const { inside, undated } = partitionByRange(payouts, range, (p) => p.createdAt);
    return { inRange: inside, undatedCount: undated.length };
  }, [payouts, range]);

  // Per-currency gross + net paid out within the window — each currency on its
  // own, never blended. Legacy payouts without a per-currency
  // breakdown are bucketed as USD, matching how the table renders them ("$").
  const rangeTotals = useMemo(() => {
    const gross: Record<string, number> = {};
    const net: Record<string, number> = {};
    for (const p of inRange) {
      const tbc = p.totalsByCurrency || {};
      if (Object.keys(tbc).length > 0) {
        for (const [cur, t] of Object.entries(tbc)) {
          gross[cur] = (gross[cur] || 0) + (t.gross || 0);
          net[cur] = (net[cur] || 0) + (t.net || 0);
        }
      } else {
        gross.usd = (gross.usd || 0) + (p.totalGross || 0);
        net.usd = (net.usd || 0) + (p.totalNet || 0);
      }
    }
    return { gross, net, count: inRange.length };
  }, [inRange]);

  const activeRangeLabel = TIME_RANGE_OPTIONS.find((o) => o.key === range)?.label ?? "All Time";

  const filtered = useMemo(() => {
    let list = [...inRange];

    // Date search filter
    if (dateSearch.trim()) {
      const q = dateSearch.trim().toLowerCase();
      list = list.filter((p) => {
        const d = new Date(p.createdAt);
        const dateStr = d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" }).toLowerCase();
        return dateStr.includes(q);
      });
    }

    // Sort
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === "date") cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      else if (sortField === "gross") cmp = a.totalGross - b.totalGross;
      else if (sortField === "avg") cmp = a.avgRequestValue - b.avgRequestValue;
      return sortDir === "desc" ? -cmp : cmp;
    });

    return list;
  }, [inRange, sortField, sortDir, dateSearch]);

  // Server totals when the server has them; the loaded rows' sum on a server
  // from before the summary route; nothing (Loading… / Couldn't load) while
  // the server's answer is missing, never a partial sum under a total's label.
  const kpis = summary.data
    ? { gross: summary.data.grossByCurrency, net: summary.data.netByCurrency, count: summary.data.count, undated: summary.data.undatedCount }
    : summary.missing
      ? { ...rangeTotals, undated: undatedCount }
      : null;

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text adm-center adm-p-40">
          Loading payouts...
        </p>
      </section>
    );
  }

  if (failed && payouts.length === 0) {
    return (
      <section className="fade-in">
        <TimeRangeBar value={range} onChange={onRangeChange} />
        <AdminLoadFailed what="payouts" onRetry={load} />
      </section>
    );
  }

  return (
    <section className="fade-in">
      <TimeRangeBar value={range} onChange={onRangeChange} />
      {range === "all" && kpis && <UndatedNote count={kpis.undated} noun="payout" />}

      {/* Per-currency totals paid out within the selected window — split by
          currency, never blended. */}
      {!kpis ? (
        <StatsPending failed={summary.failed} onRetry={summary.retry} />
      ) : (
      <div className="adm-kpi-trio">
        <div className="kpi-card kpi-card-compact">
          <div>
            <div className="kpi-label">Gross Paid Out ({activeRangeLabel})</div>
            <div className="kpi-value">{formatMoneyByCurrency(kpis.gross)}</div>
          </div>
        </div>
        <div className="kpi-card kpi-card-compact">
          <div>
            <div className="kpi-label">Net to Talent ({activeRangeLabel})</div>
            <div className="kpi-value adm-green">
              {formatMoneyByCurrency(kpis.net)}
            </div>
          </div>
        </div>
        <div className="kpi-card kpi-card-compact">
          <div>
            <div className="kpi-label">Payout Batches ({activeRangeLabel})</div>
            <div className="kpi-value">{kpis.count}</div>
          </div>
        </div>
      </div>
      )}

      <div className="adm-card">
        <div className="card-header-flex adm-mb-12">
          <h2 className="adm-section-title">All Payouts</h2>
          <input
            className="adm-search adm-search-wide"
            placeholder="Search by date (e.g. 4/12/26)..."
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
          />
        </div>

        {payouts.length === 0 ? (
          <p className="muted-text adm-center adm-p-30">
            No payouts found.
          </p>
        ) : (
          <div className="adm-scrollable-full adm-table-viewport">
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th
                    className="adm-sortable"
                    onClick={() => toggleSort("date")}
                  >
                    Date {sortArrow("date")}
                  </th>
                  <th>Time</th>
                  <th>Talent</th>
                  <th
                    className="adm-sortable"
                    onClick={() => toggleSort("gross")}
                  >
                    Gross {sortArrow("gross")}
                  </th>
                  <th>Net (Talent)</th>
                  <th># Orders</th>
                  <th
                    className="adm-sortable"
                    onClick={() => toggleSort("avg")}
                  >
                    Avg. Value {sortArrow("avg")}
                  </th>
                  <th>Avg. Response</th>
                  <th>Batch ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const dj = p.dj;
                  const avatarSrc = absMediaUrl(dj?.avatarUrl);
                  // Show gross/net per settlement currency; avg uses the batch's
                  // lead-currency symbol (the server sends avg pre-blended).
                  const hasBreakdown = Object.keys(p.totalsByCurrency || {}).length > 0;
                  const grossStr = hasBreakdown
                    ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "gross"))
                    : `$${p.totalGross.toFixed(2)}`;
                  const netStr = hasBreakdown
                    ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net"))
                    : `$${p.totalNet.toFixed(2)}`;
                  const avgStr = formatBatchAvgValue(p.totalsByCurrency, p.avgRequestValue);
                  return (
                    <tr key={p._id} className="hover-row">
                      <td className="adm-nowrap">
                        {new Date(p.createdAt).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" })}
                      </td>
                      <td className="muted-text adm-nowrap">
                        {new Date(p.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td>
                        {dj ? (
                          <div
                            className="dj-cell dj-cell-compact clickable"
                            onClick={() => navigate(`/admin/dj/${dj._id}`)}
                          >
                            {avatarSrc ? (
                              <img src={avatarSrc} className="dj-cell-avatar-sm" alt="" />
                            ) : (
                              <div className="dj-cell-placeholder-sm">
                                {dj.name?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                            <span className="dj-link-text adm-fs-82">
                              {dj.name}
                            </span>
                          </div>
                        ) : (
                          <span className="muted-text">Unknown</span>
                        )}
                      </td>
                      <td className="adm-semibold">{grossStr}</td>
                      <td className="adm-bold adm-green">
                        {netStr}
                      </td>
                      <td>
                        <span className="adm-bold">{p.ordersCount}</span>{" "}
                        <span className="adm-fs-78">
                          ({p.ordersAccepted}/{p.ordersDeclined})
                        </span>
                      </td>
                      <td>{avgStr}</td>
                      <td className="muted-text adm-fs-78">
                        {p.avgResponseMs != null ? formatDuration(p.avgResponseMs) : "—"}
                      </td>
                      <td
                        className="muted-text adm-fs-72 adm-nowrap"
                        style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}
                        title={p.batchId}
                      >
                        {p.batchId}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="adm-center adm-p-30 adm-grey">
                      No payouts match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {payouts.length < payoutsTotal && loadMoreFailed && (
              <AdminLoadFailed what="more payouts" onRetry={loadMore} />
            )}
            {payouts.length < payoutsTotal && !loadMoreFailed && (
              <button
                className="adm-btn-light adm-center-block"
                disabled={loadingMore}
                onClick={loadMore}
              >
                {loadingMore ? "Loading…" : `Load more (${payouts.length} of ${payoutsTotal})`}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
